import { employeeServices } from '@/services'
import { AttachmentUploader } from '@/shared/components/attachment-uploader'
import { Pill } from '@/shared/components/pill'
import { useCreateAttachmentReplacementCandidateMutation } from '@/store/services/attachment-replacement-candidate.service'
import { useGetEmployeeContractAttachmentQuery, useUpdateEmployeeContractAttachmentMutation } from '@/store/services/employee-contract-attachment.service'
import { StatusChip } from '@/components/status-chip'
import { attachmentChipStatuses } from '@/constants/attachment-chip-statuses'
import { getVisibleRejectedReplacementCandidate } from '@/utils/attachment-replacement'
import moment from '@/config/moment'
import {
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  Spinner,
  Typography
} from '@material-tailwind/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath, useNavigate, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuthorization } from '@/hooks'
import {
  DOCUMENTS_UPLOAD_PERMISSION,
  EMPLOYEE_UPLOAD_PERMISSION
} from '@/utils/attachment-upload-permissions'

export const PendingEmployeeContractAttachmentUpload = () => {
  const { t } = useTranslation()
  const [currentResource, setCurrentResource] = useState({})
  const [uploadPhase, setUploadPhase] = useState('idle')
  const navigate = useNavigate()
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })
  const {
    id: routeId,
    employeeId,
    attachmentId
  } = useParams()
  const currentAttachmentId = attachmentId || routeId
  const {
    data: currentAttachment = {},
    isLoading: isLoadingAttachment
  } = useGetEmployeeContractAttachmentQuery(currentAttachmentId, {
    skip: !currentAttachmentId
  })
  const location = useLocation()
  const currentUrlParams = location.search

  const [updateEmployeeContractAttachment, { isLoading }] =
    useUpdateEmployeeContractAttachmentMutation()
  const [createAttachmentReplacementCandidate, { isLoading: isCreatingReplacement }] =
    useCreateAttachmentReplacementCandidateMutation()

  useEffect(() => {
    if (!currentAttachmentId) return

    hasAccess(EMPLOYEE_UPLOAD_PERMISSION)
    if (location.pathname.startsWith('/attachments/')) {
      hasAccess(DOCUMENTS_UPLOAD_PERMISSION)
    }
  }, [currentAttachmentId, location.pathname])

  const returnPath = useMemo(() => {
    if (attachmentId && routeId) {
      return generatePath('/employees/:id/attachments', { id: routeId })
    }

    if (employeeId) {
      return generatePath('/employees/:id/attachments', { id: employeeId })
    }

    return '/attachments'
  }, [attachmentId, employeeId, routeId])

  const navigateBackWithReload = () => {
    const nextSearchParams = new URLSearchParams(currentUrlParams)
    nextSearchParams.set('attachment_reload', String(Date.now()))
    const nextQuery = nextSearchParams.toString()

    navigate(nextQuery ? `${returnPath}?${nextQuery}` : returnPath)
  }

  const onUpload = async (values) => {
    if (!values.document.filename) {
      toast.warn('Adjunta un documento antes de actualizar.')
      return
    }

    if (currentAttachment?.openReplacementCandidate?.id) {
      toast.warn('Este documento ya tiene un reemplazo en revisión o programado.')
      return
    }

    try {
      setUploadPhase('uploading')
      if (currentAttachment.status === 'approved') {
        const effectiveMode = values.replacement_effective_mode || 'immediate'

        await createAttachmentReplacementCandidate({
          replaceable_type: 'EmployeeContractAttachment',
          replaceable_id: currentAttachment.id,
          effective_mode: effectiveMode,
          effective_on: effectiveMode === 'scheduled' ? values.replacement_effective_on : null,
          expiration_date: values.expiration_date,
          document: values.document
        }).unwrap()
      } else {
        await updateEmployeeContractAttachment(values).unwrap()
      }
      setUploadPhase('returning')
      navigateBackWithReload()
    } catch (error) {
      setUploadPhase('idle')
      throw error
    }
  }

  const attachmentTypeOptions = useMemo(() => {
    return currentAttachment?.attachmentType ? [currentAttachment.attachmentType] : []
  }, [currentAttachment])

  const initialValues = useMemo(() => {
    if (!currentAttachment?.id || !currentAttachment?.attachmentType?.id) return {}

    return {
      id: currentAttachment.id,
      attachment_type_id: currentAttachment.attachmentType.id,
      document: {},
      expiration_date: ['approved', 'expired'].includes(currentAttachment.status)
        ? null
        : currentAttachment.expirationDate || null,
      period_month: currentAttachment.periodMonth,
      period_year: currentAttachment.periodYear,
      status: currentAttachment.status === 'approved' ? 'approved' : 'pending',
      replacement_effective_mode: 'immediate',
      replacement_effective_on: null
    }
  }, [currentAttachment])

  const rejectedReplacementCandidate = useMemo(
    () => getVisibleRejectedReplacementCandidate(currentAttachment),
    [currentAttachment]
  )

  const replacementEffectiveOnMax = useMemo(() => {
    if (!currentAttachment?.expirationDate) return undefined

    return moment(currentAttachment.expirationDate).add(1, 'day').format('YYYY-MM-DD')
  }, [currentAttachment?.expirationDate])

  const resourcePromise = () => {
    return employeeServices.show({
      id: currentAttachment.employeeContract.employeeId
    })
  }

  useEffect(() => {
    if (!currentAttachment.id) return

    const fetchResource = async () => {
      const { data } = await resourcePromise()
      const [resource = {}] = Object.values(data)
      setCurrentResource(resource)
    }

    fetchResource()
  }, [currentAttachment])

  const pillOptions = useMemo(() => {
    if (!currentResource.id) return []

    return [
      { label: t('common.firstName'), value: `${currentResource.name} ${currentResource.lastName}` },
      { label: t('documentNumber.one'), value: currentResource.documentNumber },
      { label: t('contract.one'), value: `${currentAttachment?.employeeContract?.contract?.id} - ${currentAttachment?.employeeContract?.contract?.name} ` },
      { label: t('contractor.one'), value: `${currentAttachment?.employeeContract?.contract?.company?.name}` }
    ]
  }, [currentAttachment, currentResource, t])

  const isReady = attachmentTypeOptions.length > 0 && Object.keys(initialValues).length > 0
  const isBusy = isLoading || isCreatingReplacement || uploadPhase !== 'idle'
  const loadingMessage = uploadPhase === 'returning'
    ? 'Actualizando listado...'
    : 'Subiendo documento...'

  return (
    <Dialog open size='lg'>
      <DialogHeader>Carga de documentos</DialogHeader>

      <DialogBody className='flex gap-4'>
        {pillOptions.map((pillOption) => (
          <Pill key={pillOption.label} {...pillOption} />
        ))}
      </DialogBody>

      <DialogBody className='relative'>
        {isBusy && (
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]'>
            <div className='flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-md'>
              <Spinner className='h-5 w-5' />
              <Typography>{loadingMessage}</Typography>
            </div>
          </div>
        )}
        {rejectedReplacementCandidate?.id && (
          <div className='mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-900'>
            <div className='mb-2'>
              <StatusChip
                options={attachmentChipStatuses}
                value='replacement_rejected'
              />
            </div>
            <Typography className='text-sm font-medium'>
              El último archivo de reemplazo fue rechazado.
            </Typography>
            <Typography className='text-sm'>
              {rejectedReplacementCandidate.reviewComment || 'Sin comentario de revisión.'}
            </Typography>
          </div>
        )}
        {isReady
          ? (
            <AttachmentUploader
              attachableType={currentAttachment.attachableType}
              attachmentTypes={attachmentTypeOptions}
              initialValues={initialValues}
              showDefaultHeader={false}
              onUpload={onUpload}
              isDisabled
              restrictPastExpirationDates
              expirationDateNotice={t('common.uploadDocuments.pendingExpirationNotice', {
                entity: 'la persona'
              })}
              replacementModeEnabled={currentAttachment.status === 'approved'}
              replacementEffectiveOnMax={replacementEffectiveOnMax}
            >
              {(handleSubmit) => (
                <div className='flex justify-between'>
                  <Link to={`${returnPath}${currentUrlParams}`}>
                    <Button
                      variant='text'
                      disabled={isBusy}
                    >
                      Cancelar
                    </Button>
                  </Link>
                  <Button loading={isBusy} disabled={isBusy || !!currentAttachment?.openReplacementCandidate?.id} onClick={handleSubmit}>
                    Cargar archivo
                  </Button>
                </div>
              )}
            </AttachmentUploader>
            )
          : (
            <div className='flex min-h-48 items-center justify-center gap-3 text-gray-600'>
              <Spinner className='h-5 w-5' />
              <Typography>
                {isLoadingAttachment ? 'Cargando formulario...' : 'Preparando formulario...'}
              </Typography>
            </div>
            )}
      </DialogBody>
    </Dialog>
  )
}
